import { QUESTIONNAIRE_TEMPLATE_NAME_MAX, type QuestionnaireTemplate } from '@nexus/core';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  FormField,
  IconButton,
  Input,
  ListSkeleton,
  moveItem,
  ReorderButtons,
  SettingsSection,
  toast,
  UiLink,
} from '@nexus/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '../api/client';
import { useModules } from '../modules/context';
import { moduleSettingsPath } from '../modules/settings';
import {
  questionnairePath,
  questionnaireTemplatesQuery,
  useTemplateError,
  type OwnedQuestionnaire,
} from './api';

export function questionnaireEditorPath(moduleId: string, templateId: string): string {
  return `${moduleSettingsPath(moduleId)}/questionnaires/${templateId}`;
}

/**
 * Templates of the questionnaires of a module and its submodules on the settings page, for
 * `<module>.settings.manage` (docs/architecture/questionnaires.md).
 */
export function QuestionnairesSection({
  moduleId,
  questionnaires,
}: {
  moduleId: string;
  questionnaires: readonly OwnedQuestionnaire[];
}) {
  const { t } = useTranslation();
  const manifests = useModules().map((m) => m.manifest);
  function title(questionnaire: OwnedQuestionnaire): string {
    const owner = manifests.find((m) => m.id === questionnaire.owner);
    return owner && owner.id !== moduleId
      ? `${t(owner.name)}: ${t(questionnaire.label)}`
      : t(questionnaire.label);
  }
  return (
    <SettingsSection
      title={t('questionnaires.title')}
      description={t('questionnaires.description')}
    >
      {questionnaires.map((questionnaire) => (
        <TemplateList
          key={questionnaire.key}
          moduleId={moduleId}
          questionnaire={questionnaire.key}
          title={title(questionnaire)}
        />
      ))}
    </SettingsSection>
  );
}

function TemplateList({
  moduleId,
  questionnaire,
  title,
}: {
  moduleId: string;
  questionnaire: string;
  title: string;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const navigate = useNavigate();
  const reportError = useTemplateError();
  const headingId = useId();
  const query = useQuery(questionnaireTemplatesQuery(questionnaire));
  const { queryKey } = questionnaireTemplatesQuery(questionnaire);
  const base = questionnairePath(questionnaire);
  const [creating, setCreating] = useState(false);
  const [copying, setCopying] = useState<QuestionnaireTemplate | null>(null);
  const [deleting, setDeleting] = useState<QuestionnaireTemplate | null>(null);
  const [confirming, setConfirming] = useState(false);

  const refresh = () => client.invalidateQueries({ queryKey });

  const reorder = useMutation({
    mutationFn: (ids: string[]) =>
      api.put<{ items: QuestionnaireTemplate[] }>(`${base}/order`, { ids }),
    onSuccess: (answer) => client.setQueryData(queryKey, answer.items),
    onError: async (error) => {
      reportError(error);
      await refresh();
    },
  });

  const makeDefault = useMutation({
    mutationFn: (template: QuestionnaireTemplate) =>
      api.patch(`${base}/templates/${template.id}`, {
        version: template.version,
        isDefault: true,
      }),
    onSuccess: (_, template) =>
      toast.success(t('questionnaires.defaultSet', { name: template.name })),
    onError: reportError,
    onSettled: refresh,
  });

  const remove = useMutation({
    mutationFn: (template: QuestionnaireTemplate) => api.delete(`${base}/templates/${template.id}`),
    onSuccess: () => toast.success(t('questionnaires.deleted')),
    onError: reportError,
    onSettled: refresh,
  });

  const defaults = useMutation({
    mutationFn: () =>
      api.post<{ created: number; items: QuestionnaireTemplate[] }>(`${base}/defaults`),
    onSuccess: (answer) => {
      client.setQueryData(queryKey, answer.items);
      if (answer.created > 0) {
        toast.success(t('questionnaires.defaultsLoaded', { count: answer.created }));
      } else toast.info(t('questionnaires.defaultsNone'));
    },
    onError: reportError,
  });

  function move(templates: QuestionnaireTemplate[], from: number, to: number) {
    const next = moveItem(templates, from, to);
    client.setQueryData(queryKey, next);
    reorder.mutate(next.map((template) => template.id));
  }

  const templates = query.data;
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={headingId} className="font-medium text-fg">
          {title}
        </h3>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" loading={defaults.isPending} onClick={() => defaults.mutate()}>
            {t('questionnaires.loadDefaults')}
          </Button>
          <Button size="sm" icon="plus" onClick={() => setCreating(true)}>
            {t('questionnaires.add')}
          </Button>
        </div>
      </div>
      {query.isError ? (
        <p className="text-fg-muted">{t('common.loadFailed')}</p>
      ) : !templates ? (
        <ListSkeleton card={false} rows={2} trailing />
      ) : templates.length === 0 ? (
        <p className="text-fg-muted">{t('questionnaires.empty')}</p>
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line">
          {templates.map((template, index) => (
            <li key={template.id} className="flex items-center gap-2 py-2 pr-1 pl-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <UiLink
                    href={questionnaireEditorPath(moduleId, template.id)}
                    className="min-w-0 truncate font-medium text-fg hover:underline"
                  >
                    {template.name}
                  </UiLink>
                  {template.isDefault && <Badge tone="brand">{t('questionnaires.default')}</Badge>}
                  {!template.active && <Badge>{t('questionnaires.inactive')}</Badge>}
                </div>
                <p className="text-xs text-fg-muted">
                  {t('questionnaires.questionCount', { count: template.questions.length })}
                </p>
              </div>
              <ReorderButtons
                index={index}
                count={templates.length}
                onMove={(from, to) => move(templates, from, to)}
                upLabel={t('questionnaires.moveUp', { name: template.name })}
                downLabel={t('questionnaires.moveDown', { name: template.name })}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton
                    icon="ellipsis-vertical"
                    label={t('questionnaires.actions', { name: template.name })}
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    icon="pencil"
                    onSelect={() =>
                      void navigate({ to: questionnaireEditorPath(moduleId, template.id) })
                    }
                  >
                    {t('questionnaires.edit')}
                  </DropdownMenuItem>
                  <DropdownMenuItem icon="copy" onSelect={() => setCopying(template)}>
                    {t('questionnaires.copy')}
                  </DropdownMenuItem>
                  {!template.isDefault && (
                    <DropdownMenuItem icon="check" onSelect={() => makeDefault.mutate(template)}>
                      {t('questionnaires.makeDefault')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    icon="trash-2"
                    tone="danger"
                    onSelect={() => {
                      setDeleting(template);
                      setConfirming(true);
                    }}
                  >
                    {t('questionnaires.delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          ))}
        </ul>
      )}

      <NameDialog
        open={creating}
        onClose={() => setCreating(false)}
        title={t('questionnaires.createTitle')}
        confirmLabel={t('questionnaires.create')}
        initial=""
        save={(name) =>
          api.post<QuestionnaireTemplate>(`${base}/templates`, { name, questions: [] })
        }
        onSaved={async (created) => {
          toast.success(t('questionnaires.created'));
          await refresh();
          await navigate({ to: questionnaireEditorPath(moduleId, created.id) });
        }}
      />
      <NameDialog
        open={copying !== null}
        onClose={() => setCopying(null)}
        title={t('questionnaires.copyTitle')}
        confirmLabel={t('questionnaires.copy')}
        initial={copying ? t('questionnaires.copyName', { name: copying.name }) : ''}
        save={(name) =>
          api.post<QuestionnaireTemplate>(`${base}/templates/${copying!.id}/copy`, { name })
        }
        onSaved={async () => {
          toast.success(t('questionnaires.copied'));
          await refresh();
        }}
      />
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        tone="danger"
        title={t('questionnaires.deleteTitle', { name: deleting?.name })}
        description={t('questionnaires.deleteDescription')}
        confirmLabel={t('questionnaires.delete')}
        onConfirm={() => (deleting ? remove.mutateAsync(deleting) : Promise.resolve())}
      />
    </section>
  );
}

/** Name of a new template or a copy; conflicts with other names show at the field. */
function NameDialog({
  open,
  onClose,
  title,
  confirmLabel,
  initial,
  save,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  confirmLabel: string;
  initial: string;
  save: (name: string) => Promise<QuestionnaireTemplate>;
  onSaved: (template: QuestionnaireTemplate) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const formId = useId();
  const reportError = useTemplateError();
  const [name, setName] = useState(initial);
  const [error, setError] = useState<string>();
  const [shown, setShown] = useState(open);
  if (open !== shown) {
    setShown(open);
    if (open) {
      setName(initial);
      setError(undefined);
    }
  }

  const mutation = useMutation({
    mutationFn: () => save(name.trim()),
    onSuccess: async (template) => {
      onClose();
      await onSaved(template);
    },
    onError: (failure) => {
      const code = failure instanceof ApiError ? failure.message : undefined;
      if (code === 'template-name-taken' || code === 'questionnaire-templates-limit') {
        setError(t(`questionnaires.errors.${code}`));
      } else reportError(failure);
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) {
      setError(t('ui.required'));
      return;
    }
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        title={title}
        size="sm"
        footer={
          <>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" form={formId} variant="primary" loading={mutation.isPending}>
              {confirmLabel}
            </Button>
          </>
        }
      >
        <form id={formId} onSubmit={submit} noValidate>
          <FormField label={t('questionnaires.name')} required error={error}>
            {(field) => (
              <Input
                {...field}
                value={name}
                maxLength={QUESTIONNAIRE_TEMPLATE_NAME_MAX}
                autoComplete="off"
                onChange={(event) => {
                  setName(event.target.value);
                  setError(undefined);
                }}
              />
            )}
          </FormField>
        </form>
      </DialogContent>
    </Dialog>
  );
}
